import { extendTheme, theme as chakraTheme } from "@chakra-ui/react";
import {
  StyleFunctionProps,
  SystemStyleObject,
  mode,
} from "@chakra-ui/theme-tools";

export const theme = extendTheme({
  colors: {
    brand: {
      red: {
        50: "#ffe1e1",
        100: "#ffb1b1",
        200: "#ff7f7f",
        300: "#ff4c4c",
        400: "#ff1a1a",
        500: "#ff0000",
        600: "#b40000",
        700: "#810000",
        800: "#500000",
        900: "#210000",
      },
      blue: {
        50: "#ddf3ff",
        100: "#afd9ff",
        200: "#7ebfff",
        300: "#4da5ff",
        400: "#4AA4FF",
        500: "#4AA4FF",
        600: "#0059b3",
        700: "#003f81",
        800: "#002650",
        900: "#000e20",
      },
      gray: {
        50: "#e8e8e8",
        100: "#d3dbdf",
        200: "#b8c2c7",
        300: "#9ca9b1",
        400: "#80919a",
        500: "#677881",
        600: "#4f5d64",
        700: "#283237",
        800: "#20282c",
        900: "#060e14",
      },
    },
  },
  fonts: {
    heading: '"Proxima Nova", sans-serif',
    body: '"Proxima Nova", sans-serif',
  },
  components: {
    Heading: {
      baseStyle: {
        textTransform: "uppercase",
        fontWeight: "700",
        position: "relative",
        marginBottom: "5",
      },
      sizes: {
        "2xl": {
          fontSize: "22px",
        },
        xl: {
          fontSize: "18px",
        },
      },
      variants: {
        redDot: {
          paddingLeft: "22px",
          _before: {
            position: "absolute",
            content: '""',
            top: "45%",
            left: 0,
            transform: "translateY(-50%)",
            width: "12px",
            height: "12px",
            backgroundColor: "brand.red.500",
          },
        },
        grayDot: {
          paddingLeft: "22px",
          _before: {
            position: "absolute",
            content: '""',
            top: "45%",
            left: 0,
            transform: "translateY(-50%)",
            width: "12px",
            height: "12px",
            backgroundColor: "brand.gray.700",
          },
        },
        underline: {
          borderBottom: "2px solid",
          borderBottomColor: "brand.gray.50",
          paddingLeft: "22px",
          paddingBottom: "9px",
          marginBottom: "5",
          _before: {
            position: "absolute",
            content: '""',
            top: "35%",
            left: 0,
            transform: "translateY(-50%)",
            width: "12px",
            height: "12px",
            backgroundColor: "brand.gray.700",
          },
        },
      },
    },
    Button: {
      baseStyle: {
        border: "none",
      },
      variants: {
        link: {
          fontWeight: "normal",
          cursor: "pointer",
        },
        outlineRound: (props: StyleFunctionProps): SystemStyleObject => {
          const outlineProps =
            chakraTheme.components.Button.variants!.outline(props);
          const borderColor =
            props.colorScheme === "gray"
              ? mode(`gray.300`, `whiteAlpha.300`)(props)
              : "currentColor";
          return {
            ...outlineProps,
            borderColor,
            borderRadius: "100%",
            borderWidth: "medium",
          };
        },
      },
    },
    Select: {
      baseStyle: {
        field: {
          margin: 0,
        },
      },
      variants: {
        headline: {
          field: {
            borderRadius: "0",
            textTransform: "uppercase",
            fontWeight: "700",
            height: "auto",
            paddingBottom: "9px",
            borderBottom: "2px solid",
            borderTopWidth: "0",
            borderLeftWidth: "0",
            borderRightWidth: "0",
            borderColor: "brand.gray.50",
            paddingLeft: "22px",
            bg: "transparent",
            _readOnly: {
              boxShadow: "none !important",
              userSelect: "all",
            },
            _invalid: {
              borderColor: "brand.gray.50",
              boxShadow: "0px 1px 0px 0px brand.gray.50",
            },
            _focus: {
              borderColor: "brand.gray.50",
              boxShadow: "0px 1px 0px 0px brand.gray.50",
            },
          },
          icon: {
            color: "brand.gray.500",
            fontSize: "2em",
          },
        },
      },
    },
    Text: {
      baseStyle: {
        fontFamily: "'Proxima Nova', sans-serif",
      },
      variants: {
        resultTextualSummaryUnit: {
          color: "brand.gray.700",
          fontSize: "4xl",
          fontWeight: "bold",
        },
      },
    },
    FormLabel: {
      baseStyle: {
        fontFamily: "'Proxima Nova', sans-serif",
      },
    },
    Switch: {
      variants: {
        mediumSwitch: (props: StyleFunctionProps): SystemStyleObject => {
          const baseStyle = chakraTheme.components.Switch.baseStyle!(props);
          // @ts-ignore
          return {
            ...baseStyle,
            track: {
              ...baseStyle.track,
              bg: "gray.600",
              transform: "rotate(180deg)",
            },
          };
        },
      },
    },
    Slider: {
      variants: {
        outlineGray: (props: StyleFunctionProps): SystemStyleObject => {
          const baseStyle = chakraTheme.components.Slider.baseStyle!(props);
          // @ts-ignore
          return {
            ...baseStyle,
            thumb: {
              ...baseStyle.thumb,
              borderColor: "gray.300",
            },
          };
        },
      },
    },
  },
  styles: {
    global: null,
  },
});
